/**
 * actions/order-review.ts
 * desc: actions for document review
 * note: 
 */

import { getErrorMessage, interpolate, omitDeprecatedFieldKeys } from "../../utilities";
import apiUrls from '../../settings/api-urls';
import request, { requestFile, type FilePayload } from '../../utilities/request';
import type ApiRequestError from '../../utilities/error';
import { getItem, setItem } from '../../utilities/local-storage';
import constants from '../../settings/constants';
// leaf module (no React/store imports) to avoid an import cycle via the utilities barrel
import { isDataExpression } from '../../components/FxEditor/utilities/expressions';
import type { AppThunk } from '../index';
import type { OrderDto, OrderFileDto } from '../../types/api/orders';
import type { OrderSchemaDto, OrderSchemaMetadataDto } from '../../types/api/order-schemas';
import type { OrderReviewItemDto } from '../../types/api/review-queues';
import type { JsonPatchOperation } from '../../types/api/json-patch';

// the field the reviewer is editing: its column name and, for a line item, the row index
export interface ActiveField {
    name: string | null;
    index: number | null;
}

// one autocomplete suggestion: the matched row of the linked collection, keyed by column
export type AutocompleteItem = Record<string, unknown>;

// the last autocomplete answer, tagged with the requesting field so a late answer for a
// previously focused field can be told apart
export interface OrderMetadataAutocomplete {
    requestKey: string | null;
    query: string | null;
    items: AutocompleteItem[];
}

// an order file once its download URL has been fetched alongside
export type OrderFileWithUrl = OrderFileDto & { fileUrl: FilePayload['fileUrl'] };

export const IS_LIST_VIEW_ACTIVE_KEY = 'orderReview.isListViewActive';
export const SELECTED_ORDER_SCHEMA_ID_KEY = 'orderReview.selectedOrderSchemaId';

// Returns the order schema id the schema-scoped screens should load: the user's
// persisted selection, falling back to the default schema when nothing is saved.
export function getSelectedOrderSchemaId(): string {
    return (getItem(SELECTED_ORDER_SCHEMA_ID_KEY) as string | null | undefined) || constants.DEFAULT_ORDER_SCHEMA_ID;
}

export function setSelectedOrderSchemaId(orderSchemaId: string): AppThunk<void> {
    return (dispatch) => {
        // Only real ids are persisted. An env-pill expression (journey-builder env selection)
        // is session-only: persisting it would leak a value the order-review screens — which
        // read getSelectedOrderSchemaId() from localStorage — can't resolve on the next load.
        if (!isDataExpression(orderSchemaId)) {
            setItem(SELECTED_ORDER_SCHEMA_ID_KEY, orderSchemaId);
        }
        dispatch(_setSelectedOrderSchemaId(orderSchemaId));
    };
}


function _setSelectedOrderSchemaId(payload: string) {
    return {
        type: 'SET_SELECTED_ORDER_SCHEMA_ID',
        payload,
    } as const;
}


//*************************************************
//********      CLEAR Order Review.         *******
//*************************************************
export function clearOrderReview() {
    return {
        type: 'CLEAR_ORDER_REVIEW',
    } as const;
}



//*************************************************
//********      SET Active Order Tab        *******
//*************************************************
export function setActiveOrderTab(tab: string) {
    return {
        type: 'SET_ACTIVE_ORDER_TAB',
        payload: tab,
    } as const;
}



//*************************************************
//********      SET Page        *******************
//*************************************************
export function setPage(page: number) {
    return {
        type: 'SET_PAGE',
        payload: page,
    } as const;
}



//*************************************************
//********      SET Angle        ******************
//*************************************************
export function setAngle(angle: number) {
    return {
        type: 'SET_ANGLE',
        payload: angle,
    } as const;
}



//*************************************************
//********      SET Active Field      *************
//*************************************************
export function setActiveField(field: ActiveField) {
    return {
        type: 'SET_ACTIVE_FIELD',
        payload: field,
    } as const;
}



//*************************************************
//********      SET Order View             ********
//*************************************************
export function setOrderView(payload: string) {
    return {
        type: 'SET_ORDER_VIEW',
        payload: payload,
    } as const;
}



//*************************************************
//********      UPDATE Order Info        **********
//*************************************************
export function updateOrderInfo(order: OrderDto) {
    return {
        type: 'SET_ORDER_INFO',
        payload: order,
    } as const;
}



//*************************************************
//********      SET Is Settings Active      *******
//*************************************************
export function setIsSettingsActive(isActive: boolean) {
    return {
        type: 'SET_IS_SETTINGS_ACTIVE',
        payload: isActive,
    } as const;
}



//*************************************************
//****   SET Is Setting Preview Active      *******
//*************************************************
export function setIsSettingPreviewActive(isActive: boolean) {
    return {
        type: 'SET_IS_SETTING_PREVIEW_ACTIVE',
        payload: isActive,
    } as const;
}



//*************************************************
//********    SET Is List View Active       *******
//*************************************************
export function setIsListViewActive(isActive: boolean) {
    setItem(IS_LIST_VIEW_ACTIVE_KEY, isActive);
    return {
        type: 'SET_IS_LIST_VIEW_ACTIVE',
        payload: isActive,
    } as const;
}



//*************************************************
//*****   ASSIGN Order Review Item          *******
//*************************************************
export function getOrderReviewItemAssign(reviewQueueId: string, reviewItemId: string, payload: JsonPatchOperation[]): AppThunk<Promise<OrderReviewItemDto>> {
    return (dispatch) => {
        const url = interpolate(apiUrls.REVIEW_QUEUE_ITEM, { reviewQueueId, reviewItemId });
        const opts = {
            method: 'PATCH',
            body: JSON.stringify(payload),
        };

        dispatch(_getOrderReviewItemAssignRequest());

        return request<OrderReviewItemDto>(url, opts)
            .then(async (results) => {
                const orderFiles = results.order?.orderFiles || [];
                const orderFilesWithUrls = await fetchOrderFiles(orderFiles, results.order.id);
                results.order.orderFiles = orderFilesWithUrls;
                dispatch(_getOrderReviewItemAssignSuccess(results));
                return results;
            })
            .catch(err => {
                dispatch(_getOrderReviewItemAssignError());
                throw err;
            });
    };
}

function _getOrderReviewItemAssignError() {
    return {
        type: 'GET_ORDER_REVIEW_ITEM_ASSIGN_ERROR',
    } as const;
}

function _getOrderReviewItemAssignRequest() {
    return {
        type: 'GET_ORDER_REVIEW_ITEM_ASSIGN_REQUEST',
    } as const;
}

function _getOrderReviewItemAssignSuccess(payload: OrderReviewItemDto) {
    return {
        type: 'GET_ORDER_REVIEW_ITEM_ASSIGN_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   GET Next Order Review Item        *******
//*************************************************
export function getOrderReviewItemNext(reviewQueueId: string): AppThunk<Promise<OrderReviewItemDto>> {
    return (dispatch) => {
        const url = interpolate(apiUrls.REVIEW_QUEUE_ITEM_NEXT, { reviewQueueId });
        const opts = {
            method: 'POST',
        };

        dispatch(_getOrderReviewItemNextRequest());

        return request<OrderReviewItemDto>(url, opts)
            .then(async (results) => {
                const orderFiles = results.order?.orderFiles || [];
                const orderFilesWithUrls = await fetchOrderFiles(orderFiles, results.order.id);
                results.order.orderFiles = orderFilesWithUrls;
                dispatch(_getOrderReviewItemNextSuccess(results));
                return results;
            })
            .catch(err => {
                dispatch(_getOrderReviewItemNextError());
                throw err;
            });
    };
}

function _getOrderReviewItemNextError() {
    return {
        type: 'GET_ORDER_REVIEW_ITEM_NEXT_ERROR',
    } as const;
}

function _getOrderReviewItemNextRequest() {
    return {
        type: 'GET_ORDER_REVIEW_ITEM_NEXT_REQUEST',
    } as const;
}

function _getOrderReviewItemNextSuccess(payload: OrderReviewItemDto) {
    return {
        type: 'GET_ORDER_REVIEW_ITEM_NEXT_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   GET Order Metadata Autocomplete   *******
//*************************************************
export function getOrderMetadataAutocomplete(orderSchemaId: string, metadataTableName: string, columnName: string, query: string, requestKey: string | null = null): AppThunk<Promise<AutocompleteItem[]>> {
    return (dispatch) => {
        const url = interpolate(apiUrls.ORDER_SCHEMA_AUTOCOMPLETE, { orderSchemaId, metadataTableName, columnName });
        const opts = {
            method: 'GET',
        };

        dispatch(_getOrderMetadataAutocompleteRequest());

        return request<AutocompleteItem[]>(`${url}?query=${encodeURIComponent(query)}`, opts)
            .then(results => {
                dispatch(_getOrderMetadataAutocompleteSuccess(results, query, requestKey));
                return results;
            })
            .catch(err => {
                dispatch(_getOrderMetadataAutocompleteError(requestKey));
                throw err;
            });
    };
}

// the failed requester's key lets the reducer resolve the slot to an empty result for that
// field — consumers treat "no response yet for this field" as loading and would otherwise
// keep showing a loading state after a failure
function _getOrderMetadataAutocompleteError(requestKey: string | null = null) {
    return {
        type: 'GET_ORDER_METADATA_AUTOCOMPLETE_ERROR',
        payload: { requestKey },
    } as const;
}

function _getOrderMetadataAutocompleteRequest() {
    return {
        type: 'GET_ORDER_METADATA_AUTOCOMPLETE_REQUEST',
    } as const;
}

// the payload carries the requesting field's key so a late response from a previously
// focused field can be recognized and ignored by the consumer
function _getOrderMetadataAutocompleteSuccess(items: AutocompleteItem[], query: string, requestKey: string | null) {
    return {
        type: 'GET_ORDER_METADATA_AUTOCOMPLETE_SUCCESS',
        payload: { requestKey, query, items },
    } as const;
}



//*************************************************
//*****   GET Order Schemas Metadata        *******
//*************************************************
// Shared request/success/error creators below are dispatched by getOrderSchema
// (the per-schema fetch); they toggle the `getOrderSchemasMetadata` loading flag
// and populate the orderSchemasMetadata slice.
function _getOrderSchemasMetadataError() {
    return {
        type: 'GET_ORDER_SCHEMAS_METADATA_ERROR',
    } as const;
}

function _getOrderSchemasMetadataRequest() {
    return {
        type: 'GET_ORDER_SCHEMAS_METADATA_REQUEST',
    } as const;
}

function _getOrderSchemasMetadataSuccess(payload: OrderSchemaMetadataDto[]) {
    return {
        type: 'GET_ORDER_SCHEMAS_METADATA_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//****   GET Order Schema (by id)          *******
//*************************************************
// Loads a specific order schema and stores its fields into the orderSchemasMetadata
// slice, so the existing order-review selectors render against that order's schema.
export function getOrderSchema(orderSchemaId: string): AppThunk<Promise<OrderSchemaDto>> {
    return (dispatch) => {
        const url = interpolate(apiUrls.ORDER_SCHEMAS_ITEM, { orderSchemaId });

        dispatch(_getOrderSchemasMetadataRequest());

        return request<OrderSchemaDto>(url, { method: 'GET' })
            .then(results => {
                dispatch(_getOrderSchemasMetadataSuccess(results.fields || []));
                return results;
            })
            .catch(err => {
                dispatch(_getOrderSchemasMetadataError());
                throw err;
            });
    };
}



//*************************************************
//****   UPDATE Order Schema Fields (PATCH)  *****
//*************************************************
// Persists the order schema's field config via PATCH /order-schemas/:id (/fields).
// Updates the orderSchemasMetadata slice optimistically so the order-review form
// reflects the change immediately.
export function updateOrderSchemaFields(orderSchemaId: string, fields: OrderSchemaMetadataDto[]): AppThunk<Promise<OrderSchemaDto>> {
    return (dispatch) => {
        const url = interpolate(apiUrls.ORDER_SCHEMAS_ITEM, { orderSchemaId });
        const opts = {
            method: 'PATCH',
            body: JSON.stringify([ { op: 'replace', path: '/fields', value: omitDeprecatedFieldKeys(fields) } ]),
        };

        dispatch(_updateOrderSchemaFieldsRequest(fields));

        return request<OrderSchemaDto>(url, opts)
            .then(results => {
                dispatch(_updateOrderSchemaFieldsSuccess(results.fields || fields));
                return results;
            })
            .catch(err => {
                dispatch(_updateOrderSchemaFieldsError());
                throw err;
            });
    };
}

function _updateOrderSchemaFieldsError() {
    return {
        type: 'UPDATE_ORDER_SCHEMA_FIELDS_ERROR',
    } as const;
}

function _updateOrderSchemaFieldsRequest(payload: OrderSchemaMetadataDto[]) {
    return {
        type: 'UPDATE_ORDER_SCHEMA_FIELDS_REQUEST',
        payload,
    } as const;
}

function _updateOrderSchemaFieldsSuccess(payload: OrderSchemaMetadataDto[]) {
    return {
        type: 'UPDATE_ORDER_SCHEMA_FIELDS_SUCCESS',
        payload,
    } as const;
}





//*************************************************
//*****   UPDATE Order Review Item        *********
//*************************************************
export function updateOrderReviewItem(reviewQueueId: string, reviewItemId: string, payload: JsonPatchOperation[], { isSilent }: { isSilent?: boolean } = {}): AppThunk<Promise<OrderReviewItemDto>> {
    return (dispatch) => {
        const url = interpolate(apiUrls.REVIEW_QUEUE_ITEM, { reviewQueueId, reviewItemId });
        const opts = {
            body: JSON.stringify(payload),
            method:'PATCH',
        };

        dispatch(_updateOrderReviewItemRequest({ isSilent }));

        return request<OrderReviewItemDto>(url, opts)
            .then(results => {
                dispatch(_updateOrderReviewItemSuccess(results, { isSilent }));
                return results;
            })
            // request rejects with ApiRequestError on HTTP failures; anything else has neither field and yields the default message
            .catch((err: ApiRequestError) => {
                // same message the toastr path derives — backs the unsaved-changes tooltip
                dispatch(_updateOrderReviewItemError({ isSilent, errorMessage: getErrorMessage(err?.errorCode, err?.context) }));
                throw err;
            });
    };
}

function _updateOrderReviewItemError({ isSilent, errorMessage }: { isSilent?: boolean; errorMessage?: string } = {}) {
    return {
        type: 'UPDATE_ORDER_REVIEW_ITEM_ERROR',
        isSilent,
        errorMessage,
    } as const;
}

function _updateOrderReviewItemRequest({ isSilent }: { isSilent?: boolean }) {
    return {
        type: 'UPDATE_ORDER_REVIEW_ITEM_REQUEST',
        isSilent
    } as const;
}

function _updateOrderReviewItemSuccess(payload: OrderReviewItemDto, { isSilent }: { isSilent?: boolean } = {}) {
    return {
        type: 'UPDATE_ORDER_REVIEW_ITEM_SUCCESS',
        payload,
        isSilent
    } as const;
}


export const fetchOrderFiles = async (orderFiles: OrderFileDto[], orderId: string): Promise<OrderFileWithUrl[]> => {
    const getFilesPromises: Promise<FilePayload>[] = [];
    orderFiles.forEach(file => {
        const fileUrl = interpolate(apiUrls.ORDER_FILE, { orderId: orderId, fileId: file.id });
        getFilesPromises.push(requestFile(fileUrl));
    });
    // Fetch all file URLs concurrently
    const fileUrls = await Promise.all(getFilesPromises);
    // Assign the fetched URLs to the orderFiles
    return orderFiles.map((file, index) => {
        return {
            ...file,
            fileUrl: fileUrls[index].fileUrl
        };
    });
};

export type OrderReviewAction =
    | ReturnType<typeof _setSelectedOrderSchemaId>
    | ReturnType<typeof clearOrderReview>
    | ReturnType<typeof setActiveOrderTab>
    | ReturnType<typeof setPage>
    | ReturnType<typeof setAngle>
    | ReturnType<typeof setActiveField>
    | ReturnType<typeof setOrderView>
    | ReturnType<typeof updateOrderInfo>
    | ReturnType<typeof setIsSettingsActive>
    | ReturnType<typeof setIsSettingPreviewActive>
    | ReturnType<typeof setIsListViewActive>
    | ReturnType<typeof _getOrderReviewItemAssignError>
    | ReturnType<typeof _getOrderReviewItemAssignRequest>
    | ReturnType<typeof _getOrderReviewItemAssignSuccess>
    | ReturnType<typeof _getOrderReviewItemNextError>
    | ReturnType<typeof _getOrderReviewItemNextRequest>
    | ReturnType<typeof _getOrderReviewItemNextSuccess>
    | ReturnType<typeof _getOrderMetadataAutocompleteError>
    | ReturnType<typeof _getOrderMetadataAutocompleteRequest>
    | ReturnType<typeof _getOrderMetadataAutocompleteSuccess>
    | ReturnType<typeof _getOrderSchemasMetadataError>
    | ReturnType<typeof _getOrderSchemasMetadataRequest>
    | ReturnType<typeof _getOrderSchemasMetadataSuccess>
    | ReturnType<typeof _updateOrderSchemaFieldsError>
    | ReturnType<typeof _updateOrderSchemaFieldsRequest>
    | ReturnType<typeof _updateOrderSchemaFieldsSuccess>
    | ReturnType<typeof _updateOrderReviewItemError>
    | ReturnType<typeof _updateOrderReviewItemRequest>
    | ReturnType<typeof _updateOrderReviewItemSuccess>;
